import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../lib/featureFlags';
import AppSwitcher from './AppSwitcher';

interface NavItem { to: string; icon: string; label: string; }

const BASE_NAV: NavItem[] = [
  { to: '/users',       icon: 'group',                  label: 'People' },
  { to: '/roles',       icon: 'admin_panel_settings',   label: 'Access' },
  { to: '/org',         icon: 'account_tree',           label: 'Organization' },
];

const CONTACTS_NAV: NavItem = {
  to: '/contacts',      icon: 'contacts',               label: 'Contacts',
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { firebaseUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const contactsEnabled = useFeatureFlag('contacts.enabled');
  const NAV: NavItem[] = contactsEnabled ? [...BASE_NAV, CONTACTS_NAV] : BASE_NAV;

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/');
  };

  const initials = (firebaseUser?.displayName || firebaseUser?.email || '?')[0].toUpperCase();

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Sidebar — softer dark stone, minimal chrome */}
      <aside className="w-60 flex-shrink-0 flex flex-col bg-stone-900 text-stone-300">

        {/* Brand */}
        <div className="px-5 pt-6 pb-7">
          <div className="flex items-center gap-2">
            <span className="material-symbols-rounded text-stone-400" style={{ fontSize: 18 }}>account_circle</span>
            <p className="text-stone-100 text-sm font-medium tracking-tight">Account</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2.5 space-y-0.5">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-stone-800 text-stone-100'
                    : 'text-stone-400 hover:text-stone-100 hover:bg-stone-800/50'
                }`
              }
            >
              <span className="material-symbols-rounded" style={{ fontSize: 18, fontVariationSettings: '"wght" 400' }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-2.5 pb-3 space-y-1">
          <AppSwitcher />
          <div className="flex items-center gap-2.5 px-3 py-2 mt-1 rounded-md hover:bg-stone-800/50 transition-colors group">
            {firebaseUser?.photoURL
              ? <img src={firebaseUser.photoURL} className="w-6 h-6 rounded-full" alt="" />
              : <div className="w-6 h-6 rounded-full bg-stone-700 flex items-center justify-center text-stone-200 text-[11px] font-medium">
                  {initials}
                </div>
            }
            <span className="text-stone-300 text-xs truncate flex-1">
              {firebaseUser?.displayName || firebaseUser?.email}
            </span>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="text-stone-500 hover:text-stone-200 transition opacity-0 group-hover:opacity-100"
              title="Sign out"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-h-screen overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
